import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useLocation, useNavigate, Outlet } from 'react-router-dom';
import { Layout, Drawer, Affix } from 'antd';
import Sidenav from './Sidenav';
import Header from './Header';
import { useAuth } from '../../context/AuthContext';
import Footer from '../Footer';
import { getCurrentUser } from '@/services/authService';
import useGetRolesOfUser from '@/hooks/admin-management/useGetRolesOfUser';
import useGetPermissions from '@/hooks/admin-management/useGetPermissions';
import { useRootStore } from '@/store/root-store';
import { useQuery } from '@tanstack/react-query';
import { api } from '@/services/authService';
import { isSuperAdmin } from '@/utils/permissions';

const { Header: AntHeader, Content, Sider } = Layout;

function Main() {
    const [visible, setVisible] = useState(false);
    const [placement, setPlacement] = useState('right');
    const [sidenavColor, setSidenavColor] = useState('#1890ff');
    const [sidenavType, setSidenavType] = useState('transparent');
    const [fixed, setFixed] = useState(false);

    const { logout, isAuthenticated } = useAuth();
    const navigate = useNavigate();
    const { pathname } = useLocation();

    const { setPermissions, setRoles, setIsUserSuperAdmin } = useRootStore();

    const currentUser = getCurrentUser();
    const currentUserId = currentUser?.id;

    // Fetch user profile to get user ID and basic role info
    const { data: userProfile } = useQuery({
        queryKey: ['admin-profile'],
        queryFn: async () => {
            const response = await api.get('/admin/user-profile');
            return response.data;
        },
        staleTime: 5 * 60 * 1000,
    });

    // Get the roles of the currently signed in user
    const { data: rolesData, isPending } = useGetRolesOfUser(currentUserId);

    // Centralized super admin detection using both profile and detailed roles
    const isUserSuperAdmin = useMemo(() => {
        const result = isSuperAdmin(userProfile, rolesData?.user_roles);
        
        // Debug logging
        console.log("=== Main.tsx Role Detection Debug ===");
        console.log("userProfile:", userProfile);
        console.log("userProfile.role:", userProfile?.role);
        console.log("userProfile.is_super:", userProfile?.is_super);
        console.log("rolesData:", rolesData);
        console.log("rolesData.user_roles:", rolesData?.user_roles);
        console.log("isUserSuperAdmin result:", result);
        console.log("=== End Main.tsx Role Detection Debug ===");
        
        return result;
    }, [userProfile, rolesData]);

    // Get the permissions for the role
    const { data: permissionsData, isPending: permissionsDataIsPending, error: permissionsError } =
        useGetPermissions(rolesData?.user_roles, !isUserSuperAdmin);

    // Redirect to login if not authenticated
    useEffect(() => {
        if (
            !isAuthenticated &&
            pathname !== '/sign-in' &&
            pathname !== '/sign-up'
        ) {
            navigate('/sign-in');
        }
    }, [isAuthenticated, navigate, pathname]);

    const openDrawer = useCallback(() => setVisible((prev) => !prev), []);
    const handleFixedNavbar = useCallback((type) => setFixed(type), []);
    const handleSidenavColor = useCallback(
        (color) => setSidenavColor(color),
        []
    );
    const handleSidenavType = useCallback((type) => setSidenavType(type), []);

    const handleLogout = useCallback(async () => {
        try {
            await logout();
            navigate('/sign-in');
        } catch (error) {
            console.error('Logout error:', error);
        }
    }, [logout, navigate]);

    useEffect(() => {
        if (pathname === 'rtl') {
            setPlacement('left');
        } else {
            setPlacement('right');
        }
    }, [pathname]);

    useEffect(() => {
        if (pathname === '') {
            navigate('/dashboard');
        }
    }, [pathname, navigate]);

    useEffect(() => {
        if (Array.isArray(rolesData?.user_roles)) {
            setRoles(rolesData?.user_roles);
        }
        
        // Only set permissions if we have valid data
        if (Array.isArray(permissionsData) && permissionsData.length > 0) {
            setPermissions(permissionsData);
        } else if (permissionsError) {
            console.error('Failed to load permissions:', permissionsError);
            // Set empty permissions array as fallback
            setPermissions([]);
        } else if (isUserSuperAdmin) {
            // Super admin should have access even without permissions loaded
            console.log('Super admin detected, setting empty permissions as fallback');
            setPermissions([]);
        }

        setIsUserSuperAdmin(isUserSuperAdmin);
    }, [rolesData, permissionsData, isUserSuperAdmin, permissionsError]);

    return (
        <Layout
            className={`layout-dashboard ${
                pathname === 'profile' ? 'layout-profile' : ''
            }`}
        >
            <Drawer
                title={false}
                placement={placement === 'right' ? 'left' : 'right'}
                closable={false}
                onClose={() => setVisible(false)}
                open={visible}
                key={placement === 'right' ? 'left' : 'right'}
                width={250}
                className={`drawer-sidebar ${
                    pathname === 'rtl' ? 'drawer-sidebar-rtl' : ''
                } `}
            >
                <Layout
                    className={`layout-dashboard ${
                        pathname === 'rtl' ? 'layout-dashboard-rtl' : ''
                    }`}
                >
                    <Sider
                        trigger={null}
                        width={250}
                        theme="light"
                        className={`sider-primary ant-layout-sider-primary ${
                            sidenavType === '#fff' ? 'active-route' : ''
                        }`}
                        style={{ background: sidenavType }}
                    >
                        <Sidenav color={sidenavColor} />
                    </Sider>
                </Layout>
            </Drawer>
            <Sider
                breakpoint="lg"
                collapsedWidth="0"
                onCollapse={(collapsed, type) => {
                    console.log(collapsed, type);
                }}
                trigger={null}
                width={250}
                theme="light"
                className={`sider-primary ant-layout-sider-primary ${
                    sidenavType === '#fff' ? 'active-route' : ''
                }`}
                style={{ background: sidenavType }}
            >
                <Sidenav color={sidenavColor} />
            </Sider>
            <Layout>
                {fixed ? (
                    <Affix>
                        <AntHeader
                            className={`${fixed ? 'ant-header-fixed' : ''}`}
                        >
                            <Header
                                onPress={openDrawer}
                                name={pathname}
                                subName={pathname}
                                handleSidenavColor={handleSidenavColor}
                                handleSidenavType={handleSidenavType}
                                handleFixedNavbar={handleFixedNavbar}
                                handleLogout={handleLogout}
                                placement={null}
                            />
                        </AntHeader>
                    </Affix>
                ) : (
                    <AntHeader className={`${fixed ? 'ant-header-fixed' : ''}`}>
                        <Header
                            onPress={openDrawer}
                            name={pathname}
                            subName={pathname}
                            handleSidenavColor={handleSidenavColor}
                            handleSidenavType={handleSidenavType}
                            handleFixedNavbar={handleFixedNavbar}
                            handleLogout={handleLogout}
                            placement={null}
                        />
                    </AntHeader>
                )}

                <Content className="content-ant">
                    <Outlet />
                </Content>
                <Footer />
            </Layout>
        </Layout>
    );
}

export default Main;
